import React, { createContext, useCallback, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';
import { DEFAULT_API_TOKEN } from '../config/environment';

const TOKEN_STORAGE_KEY = 'efb_api_token';

export interface AuthContextValue {
  token: string;
  isAuthenticated: boolean;
  setToken: (t: string) => void;
  clearToken: () => void;
  /** Fetch wrapper that auto-attaches `Authorization: Bearer <token>` when set. */
  fetchWithAuth: (input: RequestInfo | URL, init?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextValue | null>(null);

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within an AuthProvider');
  return ctx;
}

function readInitialToken(): string {
  try {
    const stored = localStorage.getItem(TOKEN_STORAGE_KEY);
    return stored || DEFAULT_API_TOKEN || '';
  } catch {
    return DEFAULT_API_TOKEN || '';
  }
}

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [token, setTokenState] = useState<string>(readInitialToken);

  const setToken = useCallback((t: string) => {
    setTokenState(t);
    try {
      if (t) localStorage.setItem(TOKEN_STORAGE_KEY, t);
      else localStorage.removeItem(TOKEN_STORAGE_KEY);
    } catch { /* localStorage unavailable */ }
  }, []);

  const clearToken = useCallback(() => setToken(''), [setToken]);

  const fetchWithAuth = useCallback(
    async (input: RequestInfo | URL, init?: RequestInit): Promise<Response> => {
      const headers = new Headers(init?.headers);
      if (token && !headers.has('Authorization')) {
        headers.set('Authorization', `Bearer ${token}`);
      }
      return fetch(input, { ...init, headers });
    },
    [token],
  );

  // Install a global wrapped fetch for libraries that call the global
  // `fetch` directly (maplibre-gl, pdfjs-dist). This uses MapLibre's
  // transformRequest hook where possible; for others, a module-scoped
  // override below is exported via `getAuthHeader()`.
  useEffect(() => {
    // no-op: consumers use fetchWithAuth() explicitly
  }, [token]);

  const value = useMemo<AuthContextValue>(() => ({
    token,
    isAuthenticated: token.length > 0,
    setToken,
    clearToken,
    fetchWithAuth,
  }), [token, setToken, clearToken, fetchWithAuth]);

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

/**
 * Utility for non-React code paths (e.g. MapLibre `transformRequest`).
 * Reads the token directly from localStorage to avoid context coupling.
 */
export function getAuthHeader(): Record<string, string> {
  try {
    const t = localStorage.getItem(TOKEN_STORAGE_KEY) || DEFAULT_API_TOKEN || '';
    return t ? { Authorization: `Bearer ${t}` } : {};
  } catch {
    return {};
  }
}
